import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import type { ArticleData } from '@/mocks/articles';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://sugargoospread.buzz';
const faviconUrl = 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/1fc56da0b352904925ac780066826afe.jpeg';

function buildArticleSchema(article: ArticleData) {
  const pageUrl = `${siteUrl}/${article.slug}`;
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.subtitle,
    image: article.heroImage,
    author: {
      '@type': 'Organization',
      name: 'Sugargoo Spread',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Sugargoo Spread',
      logo: {
        '@type': 'ImageObject',
        url: faviconUrl,
      },
    },
    datePublished: '2026-08-05',
    dateModified: '2026-08-05',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': pageUrl,
    },
    inLanguage: 'en',
    isAccessibleForFree: true,
  };
  return schema;
}

function buildFAQSchema(article: ArticleData) {
  if (!article.faqs || article.faqs.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer.replace(/<[^>]*>/g, ''),
      },
    })),
  };
}

export default function ArticleLayout({ article }: { article: ArticleData }) {
  const articleSchema = buildArticleSchema(article);
  const faqSchema = buildFAQSchema(article);

  return (
    <>
      <JsonLd data={articleSchema} />
      {faqSchema && <JsonLd data={faqSchema} />}
      <Navbar />
      <main>
        <article>
          {/* Article Hero */}
          <section className="relative w-full min-h-[320px] md:min-h-[400px] flex items-center justify-center overflow-hidden">
            <img
              src={article.heroImage}
              alt={article.title}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/35"></div>
            <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center">
              <p className="text-accent-400 text-sm font-semibold tracking-wider uppercase mb-3">{article.category}</p>
              <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
                {article.title}
              </h1>
              <p className="mt-4 text-white/75 text-sm md:text-base max-w-2xl mx-auto">
                {article.subtitle}
              </p>
            </div>
          </section>

          {/* Article Content */}
          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div className="prose prose-base md:prose-lg max-w-none text-foreground-700 leading-relaxed space-y-8">
                {article.sections.map((section, idx) => (
                  <div key={idx}>
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-0">
                      {section.heading}
                    </h2>
                    {section.paragraphs.map((p, pIdx) => (
                      <p key={pIdx} dangerouslySetInnerHTML={{ __html: p }} />
                    ))}
                    {section.listItems && section.listType === 'unordered' && (
                      <ul className="list-disc pl-5 space-y-2">
                        {section.listItems.map((item, liIdx) => (
                          <li key={liIdx} dangerouslySetInnerHTML={{ __html: item }} />
                        ))}
                      </ul>
                    )}
                    {section.listItems && section.listType === 'ordered' && (
                      <ol className="list-decimal pl-5 space-y-3">
                        {section.listItems.map((item, liIdx) => (
                          <li key={liIdx} dangerouslySetInnerHTML={{ __html: item }} />
                        ))}
                      </ol>
                    )}
                  </div>
                ))}

                {/* FAQs */}
                {article.faqs && article.faqs.length > 0 && (
                  <div>
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                      Frequently Asked Questions
                    </h2>
                    {article.faqs.map((faq, fIdx) => (
                      <div key={fIdx} className="mt-6">
                        <h3 className="font-heading text-lg font-semibold text-foreground-900">
                          {faq.question}
                        </h3>
                        <p className="mt-2" dangerouslySetInnerHTML={{ __html: faq.answer }} />
                      </div>
                    ))}
                  </div>
                )}

                {/* Conclusion */}
                <div>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                    Start Shopping Smarter with Sugargoo Spreadsheet
                  </h2>
                  <p>
                    Whether you are a first-time buyer curious about international shopping or a seasoned bargain hunter looking to optimize your strategy, having the right information makes all the difference. The <strong className="text-foreground-900">Sugargoo Spreadsheet</strong> is designed to be your go-to resource — organized, regularly updated, and built to help you find exactly what you are looking for.
                  </p>
                  <p>
                    Ready to explore? Head back to our <Link to="/" className="text-primary-600 hover:text-primary-700 underline font-medium">homepage</Link> to browse all product categories, or visit the main Sugargoo Spreadsheet website at <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 underline">kaspreadsheet.com</a> to dive into the full product collection. Happy shopping!
                  </p>
                </div>
              </div>

              {/* Back to Home CTA */}
              <div className="mt-12 pt-8 border-t border-background-200/60 text-center">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-md text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  Back to Homepage
                </Link>
                <p className="mt-4 text-sm text-foreground-400">
                  Or visit the main site:{' '}
                  <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 underline">
                    kaspreadsheet.com
                  </a>
                </p>
              </div>
            </div>
          </section>

          {/* Related Articles */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-5xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-8">
                More Sugargoo Resources
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {['sugargoo-review', 'sugargoo-shipping-cost', 'sugargoo-coupon-guide', 'sugargoo-warehouse-guide', 'sugargoo-beginners-guide', 'sugargoo-qc-guide']
                  .filter(s => s !== article.slug)
                  .slice(0, 3)
                  .map(slug => {
                    const titles: Record<string, string> = {
                      'sugargoo-review': 'Sugargoo Review 2026',
                      'sugargoo-shipping-cost': 'Shipping Cost Guide',
                      'sugargoo-coupon-guide': 'Coupon & Discount Guide',
                      'sugargoo-warehouse-guide': 'Warehouse Guide',
                      'sugargoo-beginners-guide': 'Beginners Guide',
                      'sugargoo-qc-guide': 'Quality Control Guide',
                    };
                    return (
                      <Link
                        key={slug}
                        to={`/${slug}`}
                        className="flex items-center gap-3 p-4 bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-200 transition-colors group cursor-pointer"
                      >
                        <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                          <i className="ri-article-line text-primary-600 text-sm"></i>
                        </span>
                        <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 transition-colors">
                          {titles[slug] || slug}
                        </span>
                      </Link>
                    );
                  })}
              </div>
            </div>
          </section>
        </article>
      </main>
      <Footer />
    </>
  );
}